import { FolderFilled, FolderOutlined } from "@ant-design/icons"
import { Flex, Tag, Typography } from "antd"
import React, { forwardRef, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { neutral, semantic } from "../../../../components/widgets/tokens.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { indexCatalogBySpecKey } from "../../helpers/reportCatalog.ts"
import type { ShareEditorEntry } from "../../helpers/reportSharing.ts"
import type { ReportOwner, ReportVisibility } from "../../types/types.ts"
import { ReportCanvas } from "../canvas/ReportCanvas.tsx"
import { ReportFiltersSummary } from "../filters/ReportFiltersSummary.tsx"
import type { WizardGeneralInfo } from "./ReportWizardStepGeneral.tsx"
import {
  type ReportWizardShareHandle,
  ReportWizardStepShare
} from "./ReportWizardStepShare.tsx"

const { Text, Title, Paragraph } = Typography

const Card = ({
  title,
  children,
  className = ""
}: {
  title: string
  children: React.ReactNode
  className?: string
}) => (
  <section
    aria-label={title}
    className={`rounded-xl border border-solid bg-white p-4 ${className}`}
    style={{ borderColor: neutral.border }}
  >
    <Title level={5} className="mb-3 mt-0 text-start">
      {title}
    </Title>
    {children}
  </section>
)

const Row = ({
  label,
  children
}: {
  label: string
  children: React.ReactNode
}) => (
  <div className="grid grid-cols-[120px_minmax(0,1fr)] gap-3 py-1.5">
    <Text type="secondary" className="text-[13px]">
      {label}
    </Text>
    <div className="min-w-0 text-[13px]">{children}</div>
  </div>
)

interface ReportWizardStepReviewProps {
  reportId: string
  owner: ReportOwner
  general: WizardGeneralInfo
  savedVisibility: ReportVisibility
  onVisibilityChange: (visibility: ReportVisibility) => void
  shareEntries: ShareEditorEntry[] | null
  onShareEntriesChange: (entries: ShareEditorEntry[]) => void
}

/**
 * Everything the report will be created with, read back before Create Report,
 * beside the sharing controls that are saved with it. The preview is the
 * report's own renderer, read-only, on the data the viewer is allowed to see.
 */
export const ReportWizardStepReview = forwardRef<
  ReportWizardShareHandle,
  ReportWizardStepReviewProps
>(
  (
    {
      reportId,
      owner,
      general,
      savedVisibility,
      onVisibilityChange,
      shareEntries,
      onShareEntriesChange
    },
    shareRef
  ) => {
    const { t } = useTranslation()
    const definition = useAppSelector((s) => s.reportCanvas.definition)
    const catalog = useAppSelector((s) => s.reportCanvas.catalog)
    const bySpecKey = useMemo(() => indexCatalogBySpecKey(catalog), [catalog])

    const widgetNames = (definition?.widgets ?? []).map(
      (widget) =>
        widget.title ||
        bySpecKey.get(widget.spec)?.title ||
        t("reports.canvas.widgetUnavailable")
    )

    return (
      <div className="mx-auto grid w-full max-w-[1280px] grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_400px]">
        <Flex vertical gap="middle" className="min-w-0">
          <Flex vertical gap={2}>
            <Title level={3} className="m-0 text-start">
              {t("reports.wizard.review.title")}
            </Title>
            <Text type="secondary">{t("reports.wizard.review.subtitle")}</Text>
          </Flex>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card title={t("reports.wizard.review.details")}>
              <Row label={t("reports.wizard.general.name")}>
                <Text strong>{general.name.trim()}</Text>
              </Row>
              <Row label={t("reports.wizard.general.description")}>
                {general.description.trim() ? (
                  <Paragraph
                    className="m-0"
                    ellipsis={{ rows: 3, expandable: true }}
                  >
                    {general.description.trim()}
                  </Paragraph>
                ) : (
                  <Text type="secondary">
                    {t("reports.wizard.review.none")}
                  </Text>
                )}
              </Row>
              <Row label={t("reports.wizard.general.folder")}>
                {general.folder ? (
                  <span className="inline-flex items-center gap-1.5">
                    <FolderFilled
                      aria-hidden
                      style={{ color: semantic.star }}
                    />
                    {general.folder}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5">
                    <FolderOutlined
                      aria-hidden
                      style={{ color: neutral.muted }}
                    />
                    {t("reports.folders.unfiled")}
                  </span>
                )}
              </Row>
              {general.tags.length > 0 && (
                <Row label={t("reports.wizard.general.tags")}>
                  <Flex wrap="wrap" gap={4}>
                    {general.tags.map((tag) => (
                      <Tag key={tag} className="m-0">
                        {tag}
                      </Tag>
                    ))}
                  </Flex>
                </Row>
              )}
            </Card>

            <Card title={t("reports.wizard.review.filters")}>
              <ReportFiltersSummary definition={definition} />
            </Card>
          </div>

          <Card
            title={t("reports.wizard.review.widgets", {
              count: widgetNames.length
            })}
          >
            {widgetNames.length ? (
              <Flex wrap="wrap" gap={6}>
                {widgetNames.map((name, index) => (
                  <Tag key={`${name}-${index}`} className="m-0">
                    {name}
                  </Tag>
                ))}
              </Flex>
            ) : (
              <Text type="secondary">
                {t("reports.wizard.review.noWidgets")}
              </Text>
            )}
          </Card>

          <Card title={t("reports.wizard.review.preview")}>
            <div
              className="rounded-lg p-3"
              style={{ background: neutral.surface }}
            >
              <ReportCanvas readOnly />
            </div>
          </Card>
        </Flex>

        <section
          aria-label={t("reports.wizard.share.title")}
          className="rounded-xl border border-solid bg-white p-4 xl:sticky xl:top-0"
          style={{ borderColor: neutral.border }}
        >
          <ReportWizardStepShare
            ref={shareRef}
            reportId={reportId}
            owner={owner}
            visibility={general.visibility}
            savedVisibility={savedVisibility}
            onVisibilityChange={onVisibilityChange}
            entries={shareEntries}
            onEntriesChange={onShareEntriesChange}
          />
        </section>
      </div>
    )
  }
)

ReportWizardStepReview.displayName = "ReportWizardStepReview"
